import { type FormEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input } from '@/components/ui'
import { useReadingGoal, useSetReadingGoal } from '@/hooks/insights'
import type { LibraryBook } from '@/lib/insights'
import { InsightCard } from './InsightCard'

/** Expected books read by `today` to stay on track for `target` in the current year */
function expectedByNow(target: number, today: Date): number {
  const start = new Date(today.getFullYear(), 0, 1).getTime()
  const end = new Date(today.getFullYear() + 1, 0, 1).getTime()
  return (target * (today.getTime() - start)) / (end - start)
}

/** Yearly goal: progress bar plus ahead/behind pace */
export function ReadingGoalCard({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  const { data: target, isLoading } = useReadingGoal(year)
  const setGoal = useSetReadingGoal()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState('')

  const readThisYear = useMemo(
    () => books.filter((b) => b.status === 'read' && b.end_date?.startsWith(String(year))).length,
    [books, year]
  )

  const startEditing = () => {
    setDraft(target ? String(target) : '12')
    setIsEditing(true)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const value = Number.parseInt(draft, 10)
    if (!Number.isFinite(value) || value < 1) return
    await setGoal.mutateAsync({ year, target: Math.min(value, 1000) })
    setIsEditing(false)
  }

  const renderBody = () => {
    if (isLoading) return <div className="h-16 animate-pulse rounded bg-muted" />

    if (isEditing) {
      return (
        <form onSubmit={handleSubmit} className="space-y-3">
          <label htmlFor="reading-goal" className="text-sm text-muted-foreground">
            {t('insights.goal.howMany', { year })}
          </label>
          <div className="flex gap-2">
            <Input
              id="reading-goal"
              type="number"
              min={1}
              max={1000}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            />
            <Button type="submit" disabled={setGoal.isPending}>
              {t('common.save')}
            </Button>
          </div>
          <div className="flex justify-between">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:underline"
              onClick={() => setIsEditing(false)}
            >
              {t('common.cancel')}
            </button>
            {target && (
              <button
                type="button"
                className="text-xs text-error hover:underline"
                onClick={async () => {
                  await setGoal.mutateAsync({ year, target: null })
                  setIsEditing(false)
                }}
              >
                {t('insights.goal.remove')}
              </button>
            )}
          </div>
        </form>
      )
    }

    if (!target) {
      return (
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">{t('insights.goal.empty', { year })}</p>
          <Button size="sm" onClick={startEditing}>
            {t('insights.goal.set')}
          </Button>
        </div>
      )
    }

    const percent = Math.min(100, Math.round((readThisYear / target) * 100))
    const diff = Math.round(readThisYear - expectedByNow(target, new Date()))
    const remaining = Math.max(0, target - readThisYear)

    return (
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-2xl font-bold text-foreground">
            {readThisYear}
            <span className="text-base font-normal text-muted-foreground"> / {target}</span>
          </p>
          <span className="text-sm font-medium text-muted-foreground">{percent}%</span>
        </div>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full rounded-full bg-success" style={{ width: `${percent}%` }} />
        </div>
        <p className="text-xs text-muted-foreground">
          {remaining === 0
            ? t('insights.goal.reached')
            : diff > 0
              ? t('insights.goal.ahead', { count: diff })
              : diff < 0
                ? t('insights.goal.behind', { count: -diff })
                : t('insights.goal.onTrack')}
        </p>
      </div>
    )
  }

  return (
    <InsightCard icon="🎯" title={t('insights.goal.title', { year })}>
      {renderBody()}
      {target && !isEditing && (
        <button
          type="button"
          onClick={startEditing}
          className="mt-3 text-xs font-medium text-primary hover:underline"
        >
          {t('insights.goal.edit')}
        </button>
      )}
    </InsightCard>
  )
}
